import React, { useState } from 'react';
import fr from 'facet-rest';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import MenuItem from '@material-ui/core/MenuItem';
import Tooltip from '@material-ui/core/Tooltip';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import { withStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Typography from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Typography';
import IconButton from '@material-ui/core/IconButton';
import Icon from '@material-ui/core/Icon';
import { pickFunction } from 'Helpers/Affordances';
import useUserContext from 'Context/UserContext';
import Fade from '@mui/material/Fade';
import FacetFacNegative from 'Icons/Brand/FacetFacNegative';

const styles = theme => ({
  root: {
    minWidth: 250,
    borderBottom: `1px solid ${theme.palette.facet.lightGray}`,
    paddingBottom: theme.spacing(1),
    margin: theme.spacing(1, 0),
  },
  templateListTitle: {
    padding: theme.spacing(0, 2),
    marginBottom: theme.spacing(1),
    textTransform: 'uppercase',
    fontSize: theme.typography.body1.fontSize,
  },
  templateList: {
    maxHeight: 200,
    overflowY: 'scroll',
  },
  templateListNoItems: {
    padding: theme.spacing(0, 2),
    marginBottom: theme.spacing(1),
    fontSize: theme.typography.body1.fontSize,
  },
  savedTemplateItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  thumtackIcon: {
    fontSize: '0.8em',
    color: theme.palette.grey[300],
    transition: 'background 0.3s ease',
  },
  isPinned: {
    color: theme.palette.facet.primaryBlue,
  },
  personalTemplate: {
    fontSize: '1em',
    marginRight: theme.spacing(1.25),
    visibility: 'hidden',
  },
  templateLabelContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '90%',
  },
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
  facetIcon: {
    display: 'flex',
    flexShrink: 1,
    animation: `$fadeIn 1000ms ${theme.transitions.easing.easeInOut}`,
    padding: theme.spacing(0.25),
  },
});

const TemplateListItem = ({
  className,
  classes,
  listItemOnClick,
  templateRes,
  togglePinTemplate,
  listType,
  labelAttr,
}) => {
  const [templateIsPinned, setTemplateIsPinned] = useState(templateRes.attr('isPinned') === 'true');
  const togglePin = () => setTemplateIsPinned(!templateIsPinned);
  const { agentResource } = useUserContext();

  // Used for icon label styling
  const isFacetTemplate = templateRes.meta().field('content').attr('readonly') === true;

  return (
    <Fade
      in
      timeout={{ enter: 500, exit: 500 }}
    >
      <MenuItem
        className={classNames(className, classes.savedTemplateItem)}
        onClick={() => listItemOnClick(templateRes)}
        test={`${listType}-message-templates-list-item`}
        disableRipple
      >
        <Tooltip
          title={templateRes.attr(labelAttr)}
          placement={'top-start'}
          enterDelay={800}
        >
          <div className={classes.templateLabelContainer}>
            {!isFacetTemplate && <FiberManualRecordIcon className={classes.personalTemplate} />}
            {isFacetTemplate && <FacetFacNegative className={classes.facetIcon} />}
            <Typography
              test={`${listType}-message-templates-list-item-name`}
              variant="h5"
              color={'primaryGray2'}
              weight={'book'}
              noWrap
            >
              {templateRes.attr(labelAttr)}
            </Typography>
          </div>
        </Tooltip>
        {
          pickFunction(agentResource, {
            [fr.relationships.default.name]: () => {
              return null;
            },
            [fr.relationships.pinnedMessageTemplates.name || fr.relationships.pinnedMeetingTemplates.name]: () => { // <--- Toggles ON template pinning
              return togglePinTemplate && (
                <Tooltip
                  title={`${templateIsPinned ? 'Unpin' : 'Pin'} Template`}
                  placement="top"
                >
                  <IconButton
                    size={'small'}
                    onClick={(e) => {
                      e.stopPropagation(); // <-- Prevents propagation of this click event
                      togglePin();
                      togglePinTemplate(templateRes.resourceId, togglePin);
                    }}
                    disableRipple
                    test={`${templateIsPinned ? 'unpin' : 'pin'}-template-button`}
                  >
                    <Icon
                      className={classNames(classes.thumtackIcon, 'fa fa-thumb-tack', {
                        [classes.isPinned]: templateRes.attr('isPinned') === 'true',
                      })}
                      fontSize="small"
                    />
                  </IconButton>
                </Tooltip>);
            },
          })
        }
      </MenuItem>
    </Fade>
  );
};

TemplateListItem.defaultProps = {
  className: '',
  togglePinTemplate: null,
  labelAttr: 'name',
};

TemplateListItem.propTypes = {
  className: PropTypes.string,
  classes: PropTypes.object.isRequired,
  templateRes: PropTypes.object.isRequired,
  listItemOnClick: PropTypes.func.isRequired,
  togglePinTemplate: PropTypes.func,
  listType: PropTypes.string.isRequired,
  labelAttr: PropTypes.string,
};

export default withStyles(styles)(TemplateListItem);
